import wcs from '@/scripts/libs/wcs'
import { WcsStoreId } from '@/scripts/libs/wcs/types/store-id'
import { NoEcommerce } from '@/scripts/libs/no-ecommerce'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'
import WcsPdp from '@/scripts/libs/wcs/pdp'
import AlgoliaPdp from '@/scripts/libs/algolia/pdp'
import axios from 'axios'
import { sumPrices } from '@/scripts/libs/price'
import {
  ACCESSORIES,
  ALL_ELECTRONICS_PRODUCT_TYPES,
  ALTERNATIVE,
  CASE,
  CLIPON,
  KIT,
  OPTICAL,
  ROX_ELECTRONICS,
  SUN,
  VIDEO_EXTENSIONS,
} from '@/scripts/constants/pdp'
import {
  CarouselItem,
  CorrelatedLens,
  Product,
  Availability,
  Prices,
  ImageGallery,
} from '@/types/pdp'
import ProductDetails, { ProductDetail } from '@/elements/pdp/libs/product-details'
import makeDebug from 'debug'
import { StoreInformationMapper } from '@/scripts/exposed-api/StoreInformation'
import { Environment } from '@/scripts/exposed-api/types/store-information'

const debug = makeDebug('pdp.ts')
export interface ProductDetailsPageHelper {
  fetchCarousel(_: Product): Promise<null | Array<CarouselItem>>

  fetchPDPDataFromService(_: number): Promise<undefined | Product>

  fetchProductDescription(_: string): Promise<string>

  getPDPDataFromPage(): Product

  fetchAvailabilityData(_: number): Promise<unknown>

  fetchLensDetails(_: string): Promise<{ description?: unknown }>
}

export enum AccordionType {
  // eslint-disable-next-line no-unused-vars
  PRODUCT_DETAILS = 'product-details',
  // eslint-disable-next-line no-unused-vars
  PRESCRIPTION_LENSES = 'prescription-lenses',
  // eslint-disable-next-line no-unused-vars
  INSURANCE_AND_PRESCRIPTION_UPLOAD = 'insurance-and-prescription-upload',
  // eslint-disable-next-line no-unused-vars
  INCLUDED_IN_YOUR_ORDER = 'included-in-your-order',
  META_AI = 'meta-ai',
}

class PDP implements ProductDetailsPageHelper {
  readonly SIZES_MATRIX = [
    {
      label: 'XXS',
      mm: '<121',
      inches: '<4.8',
    },
    {
      label: 'XS',
      mm: '121-124',
      inches: '4.8-4.9',
    },
    {
      label: 'S',
      mm: '125-128',
      inches: '4.9-5',
    },
    {
      label: 'M',
      mm: '129-132',
      inches: '5.1-5.2',
    },
    {
      label: 'L',
      mm: '133-136',
      inches: '5.3-5.4',
    },
    {
      label: 'XL',
      mm: '137-140',
      inches: '5.4-5.5',
    },
    {
      label: 'XXL',
      mm: '>140',
      inches: '>5.5',
    },
  ]

  readonly DEFAULT_ACCORDION_ORDER = [
    AccordionType.PRODUCT_DETAILS,
    AccordionType.PRESCRIPTION_LENSES,
    AccordionType.INSURANCE_AND_PRESCRIPTION_UPLOAD,
    AccordionType.INCLUDED_IN_YOUR_ORDER,
  ]

  fetchCarousel = async (product: Product) => {
    const { searchProvider } = wcs.getStoreConfig()
    const wcsCarousel = await WcsPdp.fetchCarousel(product)

    if (searchProvider === 'algolia') {
      const algoliaCarousel = await AlgoliaPdp.fetchCarousel()

      if (algoliaCarousel) {
        const algoliaPartNumbers = algoliaCarousel.map((item) => item.productPartNumber)
        const wcsPartNumbers = wcsCarousel.map((item) => item.productPartNumber)

        const wcsPartNumbersWithoutMatch = wcsCarousel
          .filter((item) => !algoliaPartNumbers.includes(item.productPartNumber))
          .map((item) => item.productPartNumber)
        const algoliaNumbersWithoutMatch = algoliaCarousel
          .filter((item) => !wcsPartNumbers.includes(item.productPartNumber))
          .map((item) => item.productPartNumber)

        debug('wcsPartNumbersWithoutMatch', wcsPartNumbersWithoutMatch)
        debug('algoliaNumbersWithoutMatch', algoliaNumbersWithoutMatch)

        return wcsCarousel.sort((a: CarouselItem, b: CarouselItem) => {
          const firstItemIndex = algoliaCarousel.findIndex(
            (item) => item.productPartNumber === a.productPartNumber
          )
          const secondItemIndex = algoliaCarousel.findIndex(
            (item) => item.productPartNumber === b.productPartNumber
          )

          return +firstItemIndex - +secondItemIndex
        })
      }
    }

    return wcsCarousel
  }

  fetchPDPDataFromService = async (catEntryId: number): Promise<Product | undefined> => {
    return WcsPdp.fetchPDPDataFromService(catEntryId)
  }

  fetchProductDescription = async (parentCatEntryId: string) => {
    return WcsPdp.fetchProductDescription(parentCatEntryId)
  }

  getPDPDataFromPage = () => {
    return WcsPdp.getPDPDataFromPage()
  }

  fetchAvailabilityData = async (catEntryId: number) => {
    return WcsPdp.fetchAvailabilityData(catEntryId)
  }

  fetchLensDetails = async (cLens: string) => {
    return WcsPdp.fetchLensDetails(cLens)
  }

  isStickyBarAddPrescriptionLensesButtonEnabled = (
    productType: string,
    isRoxable: boolean
  ): boolean => {
    const { pdp } = wcs.getStoreConfig()
    return (
      pdp.isStickyBarAddPrescriptionLensesButtonEnabled &&
      [OPTICAL, ROX_ELECTRONICS].includes(productType) &&
      isRoxable
    )
  }

  customizeIsEnabled = (url: string, productType: string): boolean => {
    if (!url) return false

    return (
      !(!WcsStoreId.currentCountryHasCustomCta() && productType === OPTICAL) &&
      !NoEcommerce.isStoreNoEcommerce()
    )
  }

  getPromoMessageByProductType = (type: string) => {
    const { pdp } = wcs.getStoreConfig()
    if (type === SUN) {
      return pdp.roxPromoMessageSun
    } else if (type === OPTICAL) {
      return pdp.roxPromoMessageEye
    } else if (ALL_ELECTRONICS_PRODUCT_TYPES.includes(type)) {
      return pdp.roxPromoMessageElectronics
    } else {
      return pdp.roxPromoMessageSun
    }
  }

  getRoxPromoMessage = (type: string) => {
    const { locale } = wcs.getStoreConfig()
    const roxPromoMessage = this.getPromoMessageByProductType(type)
    return WcsLocales.getLocalizedMessage(roxPromoMessage, locale)
  }

  getMinicartPromoMessage = () => {
    const { minicartPromoStrip, locale } = wcs.getStoreConfig()
    return WcsLocales.getLocalizedMessage(minicartPromoStrip, locale)
  }

  getCustomizeItByProductType = (type: string) => {
    const { pdp } = wcs.getStoreConfig()
    if (type === SUN) {
      return pdp?.customizeItPromoMessageSun
    } else if (type === OPTICAL) {
      return pdp?.customizeItPromoMessageEye
    } else {
      return pdp?.customizeItPromoMessageSun
    }
  }

  getCustomizeItPromoMessage = (type: string) => {
    const { locale } = wcs.getStoreConfig()
    const customizeItPromoMessage = this.getCustomizeItByProductType(type)
    return WcsLocales.getLocalizedMessage(customizeItPromoMessage, locale)
  }

  findLensByType = (
    correlatedLenses: Array<CorrelatedLens>,
    type: string
  ): CorrelatedLens | undefined => {
    return correlatedLenses.find((lens) => lens.type === type)
  }

  isPrescriptionAvailable = (product: Product, availability: Availability): boolean => {
    const { roxLensPanel } = wcs.getStoreConfig()

    const productIsAvailable =
      !product.isLightJson &&
      availability?.isAddToBagEnabled &&
      !availability?.isEmailMeEnabled &&
      !!product.roxFrameCatEntryId

    return roxLensPanel.isEnabled && productIsAvailable
  }

  mapProductDetailsFeatures = (
    product: Product,
    translation: () => '',
    isNoEcommerce: boolean
  ): Array<ProductDetail> => {
    return ProductDetails.mapDetails(product, translation, isNoEcommerce)
  }

  getLeftBadgeIcon = (product: Product): string => {
    const isTransition = new RegExp(/TRANSITIONS/gi).test(product.features.lenses.treatment)
    if (product.isCollectionSustainable) {
      return 'sustainable-badge-black'
    }
    /*
    if (ALL_ELECTRONICS_PRODUCT_TYPES.includes(product.type)) {
      return 'smart-glasses-new'
    }*/
    if (product.isPolarized || product.catEntryId === product?.polarizedLens?.catEntryId) {
      return 'sunglasses-polarized-black'
    }
    if (product.modelName.includes('TRANSITIONS') || isTransition) {
      return 'transitions-icon'
    }
    return ''
  }

  mapHingeToHingeImage = () => {
    return this.mapImageFromMediaServer('FrameSize')
  }

  mapMeasurementsImage = () => {
    return this.mapImageFromMediaServer('Measurements')
  }

  private mapImageFromMediaServer = (imageName: string) => {
    const { locale } = wcs.getStoreConfig()

    const country = locale.split('-')[1].toLowerCase()

    return `https://media.ray-ban.com/size-guide/assets/${locale}/${imageName}_${country}.png`
  }

  mapAndOrderAccordions = (productType: string, isNoEcommerce: boolean, isRoxable: boolean) => {
    const accordions = []

    accordions.push({
      name: AccordionType.PRODUCT_DETAILS,
      openByDefault:
        [SUN, CASE, KIT, CLIPON, ...ALL_ELECTRONICS_PRODUCT_TYPES].includes(productType) ||
        (productType === OPTICAL && !isRoxable),
    })

    if (
      [SUN, OPTICAL, ...ALL_ELECTRONICS_PRODUCT_TYPES].includes(productType) &&
      !isNoEcommerce &&
      isRoxable
    ) {
      accordions.push({
        name: AccordionType.PRESCRIPTION_LENSES,
        openByDefault: productType === OPTICAL,
      })

      accordions.push({
        name: AccordionType.INSURANCE_AND_PRESCRIPTION_UPLOAD,
        openByDefault: false,
      })
    }

    if ([SUN, OPTICAL, ...ALL_ELECTRONICS_PRODUCT_TYPES].includes(productType) && !isNoEcommerce) {
      accordions.push({
        name: AccordionType.INCLUDED_IN_YOUR_ORDER,
        openByDefault: false,
      })
    }

    const orderOfAccordions = this.getOrderOfAccordionsByProductType(productType)

    return accordions.sort((currentAccordion, nextAccordion) => {
      return (
        orderOfAccordions.indexOf(currentAccordion.name) -
        orderOfAccordions.indexOf(nextAccordion.name)
      )
    })
  }

  getOrderOfAccordionsByProductType = (type: string) => {
    const { pdp } = wcs.getStoreConfig()
    if (type === SUN) {
      return pdp?.orderOfAccordionsSun
    } else if (type === OPTICAL) {
      return pdp?.orderOfAccordionsEye
    } else if (ALL_ELECTRONICS_PRODUCT_TYPES.includes(type)) {
      return pdp?.orderOfAccordionsElectronics
    }
    return this.DEFAULT_ACCORDION_ORDER
  }

  calculateListPrice = ({
    productPrices,
    lensListPrice,
    frameListPrice,
  }: {
    productPrices: Prices
    lensListPrice: string
    frameListPrice: string
  }): string => {
    if (lensListPrice || frameListPrice) {
      const sum = sumPrices(lensListPrice, frameListPrice) ?? ''
      return sum
    }
    if (productPrices) {
      const { listPrice } = productPrices
      return listPrice
    }
    return ''
  }

  calculateDiscountedPrice = ({
    productPromotionPrice,
    lensListPrice,
    lensPromotionPrice,
    frameListPrice,
    framePromotionPrice,
  }: {
    productPromotionPrice: string
    lensListPrice: string
    lensPromotionPrice: string
    frameListPrice: string
    framePromotionPrice: string
  }): string => {
    if (lensPromotionPrice && framePromotionPrice) {
      return sumPrices(lensPromotionPrice, framePromotionPrice) ?? ''
    }

    if (lensPromotionPrice && frameListPrice) {
      return sumPrices(lensPromotionPrice, frameListPrice) ?? ''
    }

    if (lensListPrice && framePromotionPrice) {
      return sumPrices(lensListPrice, framePromotionPrice) ?? ''
    }

    if (lensListPrice && frameListPrice) {
      return sumPrices(lensListPrice, frameListPrice) ?? ''
    }

    if (productPromotionPrice) {
      return productPromotionPrice
    }

    return ''
  }

  calculateBrazilInstallmentsPrice = ({
    promotionPrice,
    productPrice,
  }: {
    promotionPrice: string
    productPrice: string
  }): string => {
    if (wcs.isBrazil()) {
      return promotionPrice ?? productPrice
    }

    return ''
  }

  findStylePlpUrl = (product: Product): string => {
    if (product.type === SUN && product.isJunior) {
      const url = wcs.getEndPoint('sunglassesJuniorURL')
      return url
    } else if (product.type === OPTICAL && product.isJunior) {
      const url = wcs.getEndPoint('eyeglassesJuniorURL')
      return url
    } else if (product.type === SUN) {
      const url = wcs.getEndPoint('sunglassesURL')
      return url
    } else if (product.type === OPTICAL) {
      const url = wcs.getEndPoint('eyeglassesURL')
      return url
    } else if (product.type === ACCESSORIES) {
      const url = wcs.getEndPoint('accessoriesURL')
      return url
    } else if (ALL_ELECTRONICS_PRODUCT_TYPES.includes(product.type)) {
      const url = wcs.getEndPoint('storiesURL')
      return url
    } else return ''
  }

  redirectToStoreLocatorPage = () => {
    const { findStoreUrl } = wcs.getStoreConfig()
    this.redirectTo(findStoreUrl)
  }

  redirectToPLP = (product: Product) => {
    const url = this.findStylePlpUrl(product)
    this.redirectTo(url)
  }

  redirectTo = (url: string) => {
    window.location.href = url
  }

  sanitizeAvailabilityMessage = (message: string) => {
    return message.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
  }

  sanitizeBridgeAndNosepadsTranslationKey = (key: string) => {
    switch (key) {
      case 'adjustable_nosepads':
        return 'adjustable_nose_pads'
      case 'low_bridge_fit':
        return 'low_bridge'
      case 'high_bridge_fit':
        return 'high_bridge'
      case 'universal_fit':
        return 'universal_fit'
      default:
        return ''
    }
  }

  findInStoreIsEnabled = () => {
    const { pdp } = wcs.getStoreConfig()
    return pdp.findInStoreIsEnabled
  }

  estimatedDeliveryDateIsVisible = () => {
    const { pdp } = wcs.getStoreConfig()
    return pdp.estimatedDeliveryDateIsVisible
  }

  sizeAdvisorIsEnabled = () => {
    const { frameGenius } = wcs.getStoreConfig()
    return frameGenius.isSizeAdvisorEnabled
  }

  public isVideo(src: string) {
    return VIDEO_EXTENSIONS.some((ext) => src.includes(ext))
  }

  public transitionImage(images: Array<ImageGallery>): {
    images?: Array<ImageGallery>
    selectedImage?: ImageGallery
  } {
    const imagesWithParams = images || []
    const tryImageThirdPosition = images[1]
    const tryImageFirstPosition = images[0]
    if (!this.isVideo(tryImageFirstPosition.src) && tryImageThirdPosition?.view === ALTERNATIVE) {
      imagesWithParams.splice(1, 1)
      return {
        images: imagesWithParams,
        selectedImage: tryImageThirdPosition,
      }
    }
    return {
      images: imagesWithParams,
    }
  }
  public async getMoCoAvailability(model: any): Promise<any> {
    const env = StoreInformationMapper.mapEnvironment() || ''
    const isProd = env === Environment.PROD
    const urlEnv = isProd ? 'prod' : 'test'
    const locale = window.wcs_config?.locale || 'en-US'
    const country = WcsLocales.mapCountryCode(locale)
    const url = `https://images.ray-ban.com/extra/image/rxcbe/${urlEnv}/availability/RB/${country}/availability_${model}.json`
    try {
      return await axios.get(url)
    } catch (e) {
      console.log(e)
      return { data: {} }
    }
  }
}

export default new PDP()
